import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { getDocumentVideo } from '../../../../../../graphql-client/Queries';
import { useParams } from 'react-router-dom';
import NotificationError from '../../../../../NotificationError';
import { ConvertToCloudFrontUrl } from '../../../../../../utils/ConvertToCloudFrontUrl';
import { getCleanFileName, getFileExtension } from '../../../../../../utils/FileUtils';
import PDFViewer from './PDFViewer';
import QuizVideoPlaying from './QuizVideoPlaying';
import QuizDocxPlaying from './QuizDocxPlaying';

/**
 * Component chính quản lý việc hiển thị document của video
 * Hỗ trợ 2 loại file:
 * - PDF: Hiển thị PDF viewer
 * - Excel: Hiển thị quiz
 * - DOCX: Hiển thị quiz theo quy ước free-text
 */
export default function DocumentVideoPlaying() {
    const { id } = useParams();
    const [documentUrl, setDocumentUrl] = useState(null);
    const [fileType, setFileType] = useState(null);
    const [fileName, setFileName] = useState('document.pdf');

    // Fetch document data từ GraphQL
    const { loading, error, data } = useQuery(getDocumentVideo, {
        variables: { id },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true
    });

    // Reset state khi id thay đổi (chuyển video)
    useEffect(() => {
        setDocumentUrl(null);
        setFileType(null);
        setFileName('document.pdf');
    }, [id]);

    // Effect: Xử lý data khi query thành công
    useEffect(() => {
        if (data?.video?.document) {
            // Convert URL sang CloudFront CDN
            const cdnUrl = ConvertToCloudFrontUrl(data.video.document);
            setDocumentUrl(cdnUrl);
            
            // Xác định loại file (pdf/excel/docx)
            const fileExt = getFileExtension(data.video.document);
            setFileType(fileExt);
            
            // Lấy tên file sạch để hiển thị và download
            const cleanName = getCleanFileName(data.video.document);
            setFileName(cleanName);
        } else if (data?.video) {
            // Video đã load nhưng không có document - reset state
            setDocumentUrl(null);
            setFileType(null);
            setFileName('document.pdf');
        }
    }, [data]);

    // Loading state
    if (loading) {
        return (
            <div className="tab-pane documentVideo active" id="document" role="tabpanel">
                <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Đang tải...</span>
                    </div>
                    <p className="mt-2">Đang tải tài liệu...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="tab-pane documentVideo active" id="document" role="tabpanel">
                <div className="alert alert-danger" role="alert">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Lỗi: {error.message}
                </div>
            </div>
        );
    }

    // Empty state - Không có tài liệu
    if (!documentUrl || !fileType) {
        return (
            <div className="tab-pane documentVideo active" id="document" role="tabpanel">
                <NotificationError text="Chưa có tài liệu nào." />
            </div>
        );
    }

    // Render PDF Viewer
    if (fileType === 'pdf') {
        return (
            <div className="tab-pane documentVideo active" id="document" role="tabpanel">
                <PDFViewer 
                    pdfUrl={documentUrl}
                    fileName={fileName}
                />
            </div>
        );
    }

    // Render Quiz (Excel)
    if (fileType === 'excel') {
        return (
            <div className="tab-pane documentVideo active" id="document" role="tabpanel">
                <QuizVideoPlaying excelUrl={documentUrl} />
            </div>
        );
    }

    // Render Quiz (DOCX free-text)
    if (fileType === 'docx') {
        return (
            <div className="tab-pane documentVideo active" id="document" role="tabpanel">
                <QuizDocxPlaying docxUrl={documentUrl} />
            </div>
        );
    }

    // Unsupported file type
    return (
        <div className="tab-pane documentVideo active" id="document" role="tabpanel">
            <NotificationError text="Định dạng file không được hỗ trợ." />
        </div>
    );
}